import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import SlideBase from './SlideBase'
import WrappedVideoBackground from '../WrappedVideoBackground'
import WrappedImageBackground from '../WrappedImageBackground'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import InteractiveZone from '../InteractiveZone'
import { WRAPPED_SLIDES } from '../../config/slides'
import useOfferList from 'hooks/Offers/useOfferList'
import useOfferMetaDataList from 'hooks/Offers/useOfferMetaDataList'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'

const ButtonContainer = styled(InteractiveZone)`
  margin-top: ${rem(8)};
`

// Get video config for this slide (static, doesn't depend on runtime data)
const SLIDE_VIDEO = WRAPPED_SLIDES.find(s => s.id === 'prediction')?.video
const SLIDE_ID = 'prediction' as const

function PredictionSlide() {
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const isCurrentSlide = useWrappedStorySelector(state => state.currentSlide?.id === SLIDE_ID)

  const prediction = data?.prediction
  const activeBooking = data?.activeBooking

  const imageId = activeBooking?.imageId ?? prediction?.imageId

  // Use image if available, otherwise fall back to video
  const background = useMemo(() => {
    if (imageId) {
      return <WrappedImageBackground imageId={imageId} kenBurnsVariant="zoom" isActive={isCurrentSlide && !isPaused} />
    } else if (SLIDE_VIDEO) {
      return <WrappedVideoBackground video={SLIDE_VIDEO} isActive={isCurrentSlide} />
    }
    return undefined
  }, [imageId, isCurrentSlide, isPaused])

  const filters: App.OfferListFilters = {
    offerTypes: [
      'hotel',
    ],
    isStream: true,
    destinationId: prediction?.destinationId,
    sortBy: 'value',
    limit: 1,
  }

  // Get savings percentage for prediction
  const predictionList = useOfferList(filters, {
    disabled: !!(!prediction || activeBooking),
  })
  const meta = useOfferMetaDataList(filters)
  const lowestPrice = meta?.[0]?.pricing.lowestPrice ?? 0
  const lowestPriceValue = meta?.[0]?.pricing.lowestPriceValue ?? 0
  const predictionSavingsPercentage = getValueOffPercent(lowestPriceValue, lowestPrice)
  const showSavings = predictionSavingsPercentage >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  // Show active booking variant if user has one
  if (activeBooking) {
    return (
      <SlideBase background={background}>
        <FadeBackground>
          <Group direction="vertical" verticalAlign="end" gap={12} fullHeight>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">WHAT'S NEXT</Caption>
              <Heading variant="heading5"><b>Your dream {activeBooking.destination} trip is getting closer every day.</b></Heading>
            </Group>
            <Heading variant="heading1alt">{activeBooking.daysToGo} days to go!</Heading>
            <ButtonContainer>
              <TextButton
                fit="full-width"
                shape="round"
                kind="primary"
                target="_blank"
                to={`/trip-planner/generate-trip?orderId=${activeBooking.orderId}`}
              >
                Start planning your escape
              </TextButton>
            </ButtonContainer>
          </Group>
        </FadeBackground>
      </SlideBase>
    )
  } else if (prediction) {
    return (
      <SlideBase background={background}>
        <FadeBackground>
          <Group direction="vertical" verticalAlign="end" gap={12} fullHeight>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">WHAT'S NEXT</Caption>
              <Heading variant="heading5"><b>This year, a trip to</b></Heading>
            </Group>
            <Group direction="vertical" gap={4}>
              <Heading variant="heading1alt">{prediction.destinationName} is calling!</Heading>
              <BodyText variant="medium">
                {predictionList.offerCount ?? 'Many'} amazing offers{' '}
                {showSavings ? `with savings up to ${predictionSavingsPercentage}%.` : 'available'}
              </BodyText>
            </Group>
            <ButtonContainer>
              <TextButton
                fit="full-width"
                shape="round"
                kind="primary"
                target="_blank"
                to={prediction.searchUrl}
              >
                Explore {prediction.destinationName}
              </TextButton>
            </ButtonContainer>
          </Group>
        </FadeBackground>
      </SlideBase>
    )
  } else {
    return (
      <SlideBase background={background}>
        <FadeBackground>
          <Group direction="vertical" verticalAlign="end" gap={12} fullHeight>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">WHAT'S NEXT</Caption>
              <Heading variant="heading5"><b>Ready for your next trip?</b></Heading>
            </Group>
            <Heading variant="heading1alt">Start planning your perfect escape for 2026</Heading>
            <ButtonContainer>
              <TextButton
                fit="full-width"
                shape="round"
                kind="primary"
                target="_blank"
                to="/search?destinationName=Anywhere&destinationId=le_d41d8cd98f00b204e9800998ecf8427e&adults=2&children=none&campaigns=Best+of+2025"
              >
                Browse Best of 2025
              </TextButton>
            </ButtonContainer>
          </Group>
        </FadeBackground>
      </SlideBase>
    )
  }
}

export default React.memo(PredictionSlide)
